/**
 * useInboxPageRealtime - Hook centralizado de Realtime para a página de Inboxes
 * 
 * ARQUITETURA:
 * - Centraliza TODAS as subscriptions Realtime da página de Inboxes em um ÚNICO canal
 * - Escuta mudanças nas tabelas `provider_instances` e `inboxes`
 * - Invalida caches do React Query para propagar atualizações a todos os componentes
 * - Deve ser chamado UMA ÚNICA VEZ no componente raiz da página (Inboxes.tsx)
 * 
 * MOTIVAÇÃO:
 * - Antes, cada instância de useInstances/useInboxes criava seu próprio canal Realtime
 * - Com 5+ instâncias de useInstances e 3+ de useInboxes, eram 8+ WebSockets duplicados
 * - Agora, 1 único canal com 2 listeners substitui todos eles
 * 
 * O React Query compartilha cache pela queryKey. Quando este hook invalida
 * ['instances', tenantId], TODAS as instâncias de useInstances recebem os dados
 * atualizados automaticamente via cache compartilhado.
 * 
 * @version 1.0.0
 * @date 2026-03-30
 * @param tenantId - ID do tenant para filtrar eventos Realtime
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado para este hook
const logger = createLogger('InboxPageRealtime');

/**
 * Chaves de cache invalidadas ao receber eventos Realtime
 * Cobre todos os hooks que consomem dados de inboxes e instâncias
 */
const INBOX_PAGE_QUERY_KEYS = [
  'instances',
  'inboxes',
  'disconnected-inboxes',
  'tenant-inbox-ids-for-realtime',
];

/**
 * Hook que centraliza subscriptions Realtime para a página de Inboxes.
 * Deve ser chamado UMA ÚNICA VEZ no componente raiz da página.
 * 
 * @param tenantId - ID do tenant ativo
 */
export function useInboxPageRealtime(tenantId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    logger.debug('Iniciando canal Realtime centralizado para página Inboxes', { tenantId });

    // Canal único para ambas as tabelas, filtrado por tenant_id
    const channel = supabase
      .channel(`inbox-page-rt-${tenantId}`)
      // Escuta mudanças em provider_instances (status, vinculação, etc.)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'provider_instances',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: provider_instances alterado', {
            eventType: payload.eventType,
            instance_id: (payload.new as any)?.id || (payload.old as any)?.id,
          });

          // Invalida todos os caches relevantes da página
          for (const key of INBOX_PAGE_QUERY_KEYS) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      )
      // Escuta mudanças em inboxes (status, criação, exclusão, etc.)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'inboxes',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: inboxes alterado', {
            eventType: payload.eventType,
            inbox_id: (payload.new as any)?.id || (payload.old as any)?.id,
          });

          // Invalida todos os caches relevantes da página
          for (const key of INBOX_PAGE_QUERY_KEYS) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      )
      .subscribe((status) => {
        logger.debug('Realtime subscription status (página Inboxes):', status);
      });

    // Cleanup ao desmontar ou mudar de tenant
    return () => {
      logger.debug('Removendo canal Realtime centralizado da página Inboxes');
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
